import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../hooks/AuthenticationProvider";
import {
  X,
  Trophy,
  Award,
  Target,
  User,
  Activity,
  ChevronDown,
  ChevronUp,
  Users,
} from "lucide-react";
import { Spinner } from "../components/index.js";

// PlayerStatsModal Component
const PlayerStatsModal = ({ isOpen, onClose }) => {
  const { organizationId, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gameHistory, setGameHistory] = useState([]);
  const [pvpStats, setPvpStats] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedGame, setSelectedGame] = useState("all");
  const [selectedMode, setSelectedMode] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [gameTypes, setGameTypes] = useState([]);
  const [timeRange, setTimeRange] = useState("all");
  const [leaderboardCount, setLeaderboardCount] = useState(0);

  // Game type mapping
  const gameTypeMap = {
    "stop-signal": "Stop Signal Challenge",
    nback: "Memory Matrix",
    "span-task": "Memory Span",
    "set-shifting": "Rule Shifter",
    "category-fluency": "Category Fluency",
    "visual-search": "Focus Finder",
    "heart-coherence": "Breath Pacer",
    "stress-inoculation": "Stress Master",
    "math-race": "Math Race",
    "reaction-time": "Speed Reactor",
    "inspection-time": "Inspection Time",
    "information-processing": "Data Stream",
    "color-clash": "Color Clash",
    "sustained-attention": "Sustained Attention",
    "soduko-challenge": "Sudoku Challenge",
  };

  // Category mapping
  const categoryMap = {
    "stop-signal": "impulse-control",
    "color-clash": "impulse-control",
    nback: "working-memory",
    "span-task": "working-memory",
    "set-shifting": "cognitive-flexibility",
    "category-fluency": "cognitive-flexibility",
    "visual-search": "attention",
    "sustained-attention": "attention",
    "heart-coherence": "stress-regulation",
    "stress-inoculation": "stress-regulation",
    "math-race": "stress-regulation",
    "reaction-time": "processing-speed",
    "inspection-time": "processing-speed",
    "information-processing": "processing-speed",
    "soduko-challenge": "random-games",
  };

  // Category colors
  const categoryColors = {
    "impulse-control": "from-purple-500 to-indigo-600",
    "working-memory": "from-blue-500 to-cyan-600",
    "cognitive-flexibility": "from-emerald-500 to-teal-600",
    attention: "from-amber-500 to-orange-600",
    "stress-regulation": "from-rose-500 to-pink-600",
    "processing-speed": "from-red-500 to-orange-600",
    "random-games": "from-yellow-400 to-orange-500",
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!organizationId || !user?.email) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Get user PvP stats
        const userDocRef = doc(
          db,
          `organizations/${organizationId}/users`,
          user.email
        );
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setPvpStats(userData.pvpStats || null);
        }

        // Get user game history
        const historyRef = collection(
          db,
          `organizations/${organizationId}/users/${user.email}/gameHistory`
        );

        // Apply time filter if needed
        let historyQuery;
        if (timeRange === "all") {
          historyQuery = query(
            historyRef,
            orderBy("timestamp", "desc"),
            limit(100)
          );
        } else {
          // Calculate date range
          const now = new Date();
          let startDate;

          if (timeRange === "week") {
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 7);
          } else if (timeRange === "month") {
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
          } else if (timeRange === "year") {
            startDate = new Date(now);
            startDate.setFullYear(now.getFullYear() - 1);
          }

          historyQuery = query(
            historyRef,
            where("timestamp", ">=", startDate),
            orderBy("timestamp", "desc"),
            limit(100)
          );
        }

        const querySnapshot = await getDocs(historyQuery);
        const history = [];
        const gameTypesSet = new Set();

        querySnapshot.forEach((doc) => {
          const data = doc.data();

          // Convert Firestore timestamp to Date object if needed
          const timestamp =
            data.timestamp instanceof Date
              ? data.timestamp
              : data.timestamp?.toDate?.() || new Date();

          const entry = {
            id: doc.id,
            ...data,
            timestamp,
            formattedDate: timestamp.toLocaleDateString(),
            formattedTime: timestamp.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            gameName: gameTypeMap[data.gameType] || data.gameType,
          };

          history.push(entry);
          gameTypesSet.add(data.gameType);
        });

        setGameHistory(history);
        setGameTypes(Array.from(gameTypesSet));

        // NEW CODE: Get Top 5 Leaderboard Rankings count
        // Only check games that the user has played
        const gameTypesArray = Array.from(gameTypesSet);
        let leaderboardCount = 0;

        // For each game type, check if user is in top 5 for any difficulty
        const leaderboardChecks = gameTypesArray.map(async (gameType) => {
          const leaderboardRef = doc(
            db,
            `organizations/${organizationId}/gameScores`,
            gameType
          );

          try {
            const leaderboardDoc = await getDoc(leaderboardRef);
            if (leaderboardDoc.exists()) {
              const leaderboardData = leaderboardDoc.data();

              // Check each difficulty's leaderboard
              if (leaderboardData.leaderboards) {
                Object.values(leaderboardData.leaderboards).forEach(
                  (leaderboard) => {
                    // Check if user is in this leaderboard
                    const inLeaderboard = leaderboard.some(
                      (entry) => entry.userId === user.email
                    );

                    if (inLeaderboard) {
                      leaderboardCount++;
                    }
                  }
                );
              }
            }
          } catch (err) {
            console.error(`Error fetching leaderboard for ${gameType}:`, err);
            // Continue with other checks even if one fails
          }
        });

        // Wait for all leaderboard checks to complete
        await Promise.all(leaderboardChecks);

        // Set the state with the leaderboard count
        setLeaderboardCount(leaderboardCount);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, [organizationId, user?.email, timeRange]);

  // Apply filters and sorting to game history
  const filteredHistory = useMemo(() => {
    return gameHistory
      .filter((game) => {
        if (selectedGame !== "all" && game.gameType !== selectedGame)
          return false;
        if (selectedMode !== "all" && game.mode !== selectedMode) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortBy === "date") {
          return sortOrder === "desc"
            ? b.timestamp - a.timestamp
            : a.timestamp - b.timestamp;
        } else if (sortBy === "score") {
          return sortOrder === "desc" ? b.score - a.score : a.score - b.score;
        }
        return 0;
      });
  }, [gameHistory, selectedGame, selectedMode, sortBy, sortOrder]);

  // Calculate stats from history
  const stats = useMemo(() => {
    if (gameHistory.length === 0) {
      return {
        totalGames: 0,
        averageScore: 0,
        highScore: 0,
        totalTime: 0,
        singlePlayerGames: 0,
        multiplayerGames: 0,
        gameTypeBreakdown: {},
        recentImprovement: 0,
      };
    }

    const totalGames = filteredHistory.length;
    const scores = filteredHistory.map((game) => game.score || 0);
    const averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    const highScore = Math.max(...scores);

    const totalTimeInSeconds = filteredHistory.reduce((total, game) => {
      return total + (game.gameLength || 0);
    }, 0);

    const singlePlayerGames = filteredHistory.filter(
      (game) => game.mode === "single"
    ).length;
    const multiplayerGames = filteredHistory.filter(
      (game) => game.mode === "multiplayer"
    ).length;

    // Game type breakdown
    const gameTypeBreakdown = {};
    filteredHistory.forEach((game) => {
      const type = game.gameType;
      if (!gameTypeBreakdown[type]) {
        gameTypeBreakdown[type] = {
          count: 0,
          totalScore: 0,
          highScore: 0,
          name: gameTypeMap[type] || type,
          category: categoryMap[type] || "unknown",
        };
      }

      gameTypeBreakdown[type].count++;
      gameTypeBreakdown[type].totalScore += game.score || 0;
      gameTypeBreakdown[type].highScore = Math.max(
        gameTypeBreakdown[type].highScore,
        game.score || 0
      );
    });

    // Calculate recent improvement (comparing last 5 games with previous 5)
    let recentImprovement = 0;
    if (filteredHistory.length >= 10) {
      const recentGames = filteredHistory.slice(0, 5);
      const previousGames = filteredHistory.slice(5, 10);

      const recentAvg =
        recentGames.reduce((sum, game) => sum + (game.score || 0), 0) / 5;
      const prevAvg =
        previousGames.reduce((sum, game) => sum + (game.score || 0), 0) / 5;

      recentImprovement = ((recentAvg - prevAvg) / prevAvg) * 100;
    }

    return {
      totalGames,
      averageScore,
      highScore,
      totalTimeInSeconds,
      singlePlayerGames,
      multiplayerGames,
      gameTypeBreakdown,
      recentImprovement,
      leaderboardCount,
    };
  }, [filteredHistory, gameTypeMap, leaderboardCount]);

  // Format time for display
  const formatTime = (seconds) => {
    if (!seconds) return "0m";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Toggle sort order
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverviewTab();
      case "history":
        return renderHistoryTab();
      case "multiplayer":
        return renderMultiplayerTab();
      default:
        return renderOverviewTab();
    }
  };

  // Render overview tab content
  const renderOverviewTab = () => {
    return (
      <div className="space-y-6">
        {/* Stats summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-md bg-purple-500/20">
                <Target size={16} className="text-purple-400" />
              </div>
              <span className="text-sm text-slate-300">Games Played</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {stats.totalGames}
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-md bg-blue-500/20">
                <Award size={16} className="text-blue-400" />
              </div>
              <span className="text-sm text-slate-300">Game Types</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {Object.keys(stats.gameTypeBreakdown).length}
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-md bg-emerald-500/20">
                <Trophy size={16} className="text-emerald-400" />
              </div>
              <span className="text-sm text-slate-300">Top 5 Rankings</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {stats.leaderboardCount || 0}
            </div>
          </div>
        </div>

        {/* Activity Calendar */}
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <h3 className="text-white font-medium mb-3">Activity History</h3>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-1">
              {Array.from({ length: 14 }).map((_, i) => {
                // Check if there's a game in the last 14 days
                const date = new Date();
                date.setDate(date.getDate() - (13 - i));
                const dateStr = date.toISOString().split("T")[0];
                const hasActivity = filteredHistory.some(
                  (game) =>
                    new Date(game.timestamp).toISOString().split("T")[0] ===
                    dateStr
                );

                return (
                  <div key={i} className="flex flex-col items-center">
                    <div
                      className={`w-6 h-6 rounded-sm ${
                        hasActivity ? "bg-purple-500/70" : "bg-slate-700/50"
                      }`}
                      title={date.toLocaleDateString()}
                    ></div>
                    {i % 2 === 0 && (
                      <span className="text-xs text-slate-500 mt-1">
                        {date.getDate()}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center text-xs text-slate-400">
              <span>Two Weeks Ago</span>
              <span>Today</span>
            </div>
          </div>
        </div>

        {/* Game Mode & Recent Play */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <h3 className="text-white font-medium mb-3">Game Modes</h3>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-blue-500/20">
                  <User size={16} className="text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-slate-300">
                      Single Player
                    </span>
                    <span className="text-sm text-white">
                      {stats.singlePlayerGames}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{
                        width: `${
                          stats.totalGames
                            ? (stats.singlePlayerGames / stats.totalGames) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-emerald-500/20">
                  <Users size={16} className="text-emerald-400" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-slate-300">Multiplayer</span>
                    <span className="text-sm text-white">
                      {stats.multiplayerGames}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full"
                      style={{
                        width: `${
                          stats.totalGames
                            ? (stats.multiplayerGames / stats.totalGames) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <h3 className="text-white font-medium mb-3">Recent Activity</h3>
            <div className="space-y-3">
              {filteredHistory.slice(0, 3).map((game, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div
                    className={`w-2 h-8 rounded-sm bg-gradient-to-b ${
                      categoryColors[categoryMap[game.gameType]] ||
                      "from-gray-500 to-gray-600"
                    }`}
                  ></div>
                  <div className="flex-1">
                    <div className="text-sm text-white">
                      {gameTypeMap[game.gameType] || game.gameType}
                    </div>
                    <div className="text-xs text-slate-400">
                      {new Date(game.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-white">
                      {game.score?.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-400">
                      {game.mode === "multiplayer" ? "PvP" : "Solo"}
                    </div>
                  </div>
                </div>
              ))}
              {filteredHistory.length === 0 && (
                <div className="text-center text-slate-400 py-2">
                  No recent games found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cognitive Skills */}
        <div>
          <h3 className="text-white font-medium mb-3">
            Cognitive Skills Breakdown
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Create a mapping of games to cognitive skill categories */}
            {[
              "attention",
              "working-memory",
              "processing-speed",
              "impulse-control",
              "cognitive-flexibility",
            ].map((skill) => {
              const gamesInCategory = Object.entries(
                stats.gameTypeBreakdown
              ).filter(([gameType]) => categoryMap[gameType] === skill);

              const totalGamesInCategory = gamesInCategory.reduce(
                (sum, [_, data]) => sum + data.count,
                0
              );

              // Skip if no games in this category
              if (totalGamesInCategory === 0) return null;

              return (
                <div
                  key={skill}
                  className="bg-slate-800 rounded-lg p-3 border border-slate-700"
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-8 rounded-sm bg-gradient-to-b ${
                          categoryColors[skill] || "from-gray-500 to-gray-600"
                        }`}
                      ></div>
                      <div>
                        <div className="text-white font-medium capitalize">
                          {skill.replace("-", " ")}
                        </div>
                        <div className="text-xs text-slate-400">
                          {totalGamesInCategory} games played
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-400">
                        {(
                          (totalGamesInCategory / stats.totalGames) *
                          100
                        ).toFixed(0)}
                        % of activity
                      </div>
                    </div>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-1.5">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${
                        categoryColors[skill] || "from-gray-500 to-gray-600"
                      }`}
                      style={{
                        width: `${Math.min(
                          100,
                          (totalGamesInCategory / stats.totalGames) * 100
                        )}%`,
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Personal Records */}
        <div>
          <h3 className="text-white font-medium mb-3">Personal Records</h3>
          <div className="space-y-3">
            {Object.entries(stats.gameTypeBreakdown).map(([type, data]) => (
              <div
                key={type}
                className="bg-slate-800 rounded-lg p-3 border border-slate-700"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-8 rounded-sm bg-gradient-to-b ${
                        categoryColors[data.category] ||
                        "from-gray-500 to-gray-600"
                      }`}
                    ></div>
                    <div>
                      <div className="text-white font-medium">{data.name}</div>
                      <div className="text-xs text-slate-400">
                        Personal Best
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-white">
                      {data.highScore.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render history tab content
  const renderHistoryTab = () => {
    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-slate-400 mb-1 block">
                Game Type
              </label>
              <select
                value={selectedGame}
                onChange={(e) => setSelectedGame(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="all">All Games</option>
                {gameTypes.map((type) => (
                  <option key={type} value={type}>
                    {gameTypeMap[type] || type}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-slate-400 mb-1 block">Mode</label>
              <select
                value={selectedMode}
                onChange={(e) => setSelectedMode(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="all">All Modes</option>
                <option value="single">Single Player</option>
                <option value="multiplayer">Multiplayer</option>
              </select>
            </div>

            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-slate-400 mb-1 block">
                Time Period
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="all">All Time</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="year">Last Year</option>
              </select>
            </div>
          </div>
        </div>

        {/* Game history list */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-2 p-3 border-b border-slate-700 bg-slate-800/80">
            <div
              className="col-span-2 flex items-center gap-1 cursor-pointer"
              onClick={() => toggleSort("date")}
            >
              <span className="text-sm font-medium text-slate-300">Date</span>
              {sortBy === "date" &&
                (sortOrder === "asc" ? (
                  <ChevronUp size={14} className="text-purple-400" />
                ) : (
                  <ChevronDown size={14} className="text-purple-400" />
                ))}
            </div>
            <div className="col-span-3 text-sm font-medium text-slate-300">
              Game
            </div>
            <div className="col-span-1 text-sm font-medium text-slate-300">
              Mode
            </div>
            <div
              className="col-span-2 flex items-center gap-1 cursor-pointer"
              onClick={() => toggleSort("score")}
            >
              <span className="text-sm font-medium text-slate-300">Score</span>
              {sortBy === "score" &&
                (sortOrder === "asc" ? (
                  <ChevronUp size={14} className="text-purple-400" />
                ) : (
                  <ChevronDown size={14} className="text-purple-400" />
                ))}
            </div>
            <div className="col-span-4 text-sm font-medium text-slate-300">
              Key Metrics
            </div>
          </div>

          {/* Table rows */}
          <div className="max-h-80 overflow-y-auto">
            {filteredHistory.length === 0 ? (
              <div className="p-6 text-center text-slate-400">
                No game history found with the selected filters.
              </div>
            ) : (
              filteredHistory.map((game, index) => {
                const category = categoryMap[game.gameType] || "unknown";
                return (
                  <div
                    key={game.id || index}
                    className="grid grid-cols-12 gap-2 p-3 border-b border-slate-700 hover:bg-slate-700/30 transition-colors"
                  >
                    <div className="col-span-2 flex items-center gap-2">
                      <div
                        className={`w-1 h-8 rounded-sm bg-gradient-to-b ${
                          categoryColors[category] ||
                          "from-gray-500 to-gray-600"
                        }`}
                      ></div>
                      <div>
                        <div className="text-sm text-white">
                          {game.formattedDate}
                        </div>
                        <div className="text-xs text-slate-400">
                          {game.formattedTime}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-3 flex items-center">
                      <span className="text-sm text-white">
                        {game.gameName}
                      </span>
                    </div>
                    <div className="col-span-1 flex items-center">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          game.mode === "multiplayer"
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-blue-500/20 text-blue-300"
                        }`}
                      >
                        {game.mode === "multiplayer" ? "PvP" : "Solo"}
                      </span>
                    </div>
                    <div className="col-span-2 flex items-center">
                      <span className="text-sm font-medium text-white">
                        {game.score?.toLocaleString()}
                      </span>
                    </div>
                    <div className="col-span-4 flex flex-wrap gap-1">
                      {/* Render game-specific metrics based on the game type */}
                      {renderGameSpecificMetrics(game)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render game-specific metrics
  const renderGameSpecificMetrics = (game) => {
    const metricBadgeStyle =
      "text-xs px-2 py-1 rounded-full bg-slate-700/80 text-slate-300";

    switch (game.gameType) {
      case "color-clash":
        return (
          <>
            {game.avgReactionTime && (
              <span className={metricBadgeStyle}>
                Avg RT: {game.avgReactionTime}ms
              </span>
            )}
            {game.correctCount && (
              <span className={metricBadgeStyle}>
                Correct: {game.correctCount}/{game.totalTrials || "-"}
              </span>
            )}
            {game.stroopInterference && (
              <span className={metricBadgeStyle}>
                Interference: {game.stroopInterference}ms
              </span>
            )}
          </>
        );

      case "inspection-time":
        return (
          <>
            {game.averageResponseTime && (
              <span className={metricBadgeStyle}>
                Avg RT: {game.averageResponseTime}ms
              </span>
            )}
            {game.correctResponses !== undefined && (
              <span className={metricBadgeStyle}>
                Correct: {game.correctResponses}/
                {game.correctResponses + (game.incorrectResponses || 0)}
              </span>
            )}
            {game.difficulty && (
              <span className={metricBadgeStyle}>Diff: {game.difficulty}</span>
            )}
          </>
        );

      case "math-race":
        return (
          <>
            {game.averageResponseTime && (
              <span className={metricBadgeStyle}>
                Avg RT: {game.averageResponseTime}ms
              </span>
            )}
            {game.correctResponses !== undefined && (
              <span className={metricBadgeStyle}>
                Correct: {game.correctResponses}/
                {game.correctResponses + (game.incorrectResponses || 0)}
              </span>
            )}
            {game.difficulty && (
              <span className={metricBadgeStyle}>Diff: {game.difficulty}</span>
            )}
          </>
        );

      case "reaction-time":
        return (
          <>
            {game.avgReactionTime && (
              <span className={metricBadgeStyle}>
                Avg RT: {game.avgReactionTime}ms
              </span>
            )}
            {game.bestReactionTime && (
              <span className={metricBadgeStyle}>
                Best RT: {game.bestReactionTime}ms
              </span>
            )}
            {game.falseStarts !== undefined && (
              <span className={metricBadgeStyle}>
                False Starts: {game.falseStarts}
              </span>
            )}
          </>
        );

      case "set-shifting":
        return (
          <>
            {game.averageResponseTime && (
              <span className={metricBadgeStyle}>
                Avg RT: {game.averageResponseTime}ms
              </span>
            )}
            {game.correctResponses !== undefined && (
              <span className={metricBadgeStyle}>
                Correct: {game.correctResponses}/
                {game.correctResponses + (game.incorrectResponses || 0)}
              </span>
            )}
            {game.perseverationErrors !== undefined && (
              <span className={metricBadgeStyle}>
                Persev. Errors: {game.perseverationErrors}
              </span>
            )}
          </>
        );

      case "span-task":
        return (
          <>
            {game.averageReactionTime && (
              <span className={metricBadgeStyle}>
                Avg RT: {Math.round(game.averageReactionTime)}ms
              </span>
            )}
            {game.longestSequence && (
              <span className={metricBadgeStyle}>
                Max Seq: {game.longestSequence}
              </span>
            )}
            {game.correctResponses !== undefined && (
              <span className={metricBadgeStyle}>
                Correct: {game.correctResponses}/
                {game.correctResponses + (game.incorrectResponses || 0)}
              </span>
            )}
          </>
        );

      case "stop-signal":
        return (
          <>
            {game.avgGoRT && (
              <span className={metricBadgeStyle}>Go RT: {game.avgGoRT}ms</span>
            )}
            {game.estimatedSSRT && (
              <span className={metricBadgeStyle}>
                SSRT: {game.estimatedSSRT}ms
              </span>
            )}
            {game.stopSuccessCount !== undefined &&
              game.stopFailCount !== undefined && (
                <span className={metricBadgeStyle}>
                  Stop: {game.stopSuccessCount}/
                  {game.stopSuccessCount + game.stopFailCount}
                </span>
              )}
          </>
        );

      case "sudoku-challenge":
        return (
          <>
            {game.timeElapsed && (
              <span className={metricBadgeStyle}>
                Time: {Math.floor(game.timeElapsed / 60)}m{" "}
                {game.timeElapsed % 60}s
              </span>
            )}
            {game.errors !== undefined && (
              <span className={metricBadgeStyle}>Errors: {game.errors}</span>
            )}
            {game.hintsUsed !== undefined && (
              <span className={metricBadgeStyle}>Hints: {game.hintsUsed}</span>
            )}
          </>
        );

      case "sustained-attention":
        return (
          <>
            {game.averageReactionTime && (
              <span className={metricBadgeStyle}>
                Avg RT: {game.averageReactionTime}ms
              </span>
            )}
            {game.hitCount !== undefined &&
              game.missedTargets !== undefined && (
                <span className={metricBadgeStyle}>
                  Hits: {game.hitCount}/{game.hitCount + game.missedTargets}
                </span>
              )}
            {game.commissionErrors !== undefined && (
              <span className={metricBadgeStyle}>
                Comms. Errors: {game.commissionErrors}
              </span>
            )}
          </>
        );

      default:
        // For games without specific metrics defined
        return (
          <>
            {game.correctResponses !== undefined && (
              <span className={metricBadgeStyle}>
                Correct: {game.correctResponses}
              </span>
            )}
            {game.difficulty && (
              <span className={metricBadgeStyle}>
                Difficulty: {game.difficulty}
              </span>
            )}
          </>
        );
    }
  };

  // Render multiplayer tab content
  const renderMultiplayerTab = () => {
    if (!pvpStats) {
      return (
        <div className="p-6 text-center text-slate-400">
          No multiplayer stats available yet. Challenge other players to build
          your stats!
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Multiplayer summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-md bg-emerald-500/20">
                <Users size={16} className="text-emerald-400" />
              </div>
              <span className="text-sm text-slate-300">Games</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {pvpStats.totalGamesPlayed || 0}
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-md bg-purple-500/20">
                <Award size={16} className="text-purple-400" />
              </div>
              <span className="text-sm text-slate-300">Wins</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {pvpStats.wins || 0}
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-md bg-blue-500/20">
                <Activity size={16} className="text-blue-400" />
              </div>
              <span className="text-sm text-slate-300">Win Rate</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {pvpStats.totalGamesPlayed
                ? Math.round((pvpStats.wins / pvpStats.totalGamesPlayed) * 100)
                : 0}
              %
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-md bg-amber-500/20">
                <Target size={16} className="text-amber-400" />
              </div>
              <span className="text-sm text-slate-300">Total Score</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {(pvpStats.totalScore || 0).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Game specific stats */}
        {pvpStats.byGame && Object.keys(pvpStats.byGame).length > 0 && (
          <div>
            <h3 className="text-white font-medium mb-3">Game Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(pvpStats.byGame).map(([gameType, data]) => {
                const category = categoryMap[gameType] || "unknown";
                return (
                  <div
                    key={gameType}
                    className="bg-slate-800 rounded-lg p-3 border border-slate-700"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-8 rounded-sm bg-gradient-to-b ${
                            categoryColors[category] ||
                            "from-gray-500 to-gray-600"
                          }`}
                        ></div>
                        <div>
                          <div className="text-white font-medium">
                            {gameTypeMap[gameType] || gameType}
                          </div>
                          <div className="text-xs text-slate-400">
                            {data.gamesPlayed} PvP games
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-medium">
                          {data.wins || 0}W-{data.losses || 0}L-
                          {data.draws || 0}D
                        </div>
                        <div className="text-xs text-slate-400">
                          {data.gamesPlayed
                            ? Math.round((data.wins / data.gamesPlayed) * 100)
                            : 0}
                          % win rate
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="text-xs text-slate-300">High Score:</div>
                      <div className="text-sm font-medium text-white">
                        {(data.highScore || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Opponent history */}
        {pvpStats.opponents && Object.keys(pvpStats.opponents).length > 0 && (
          <div>
            <h3 className="text-white font-medium mb-3">Opponents History</h3>
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="grid grid-cols-12 gap-2 p-3 border-b border-slate-700 bg-slate-800/80">
                <div className="col-span-4 text-sm font-medium text-slate-300">
                  Opponent
                </div>
                <div className="col-span-3 text-sm font-medium text-slate-300">
                  Record
                </div>
                <div className="col-span-3 text-sm font-medium text-slate-300">
                  Win Rate
                </div>
                <div className="col-span-2 text-sm font-medium text-slate-300">
                  Last Played
                </div>
              </div>

              <div className="max-h-60 overflow-y-auto">
                {Object.entries(pvpStats.opponents).map(([email, data]) => {
                  const lastPlayed = data.lastPlayed?.toDate?.() || new Date();
                  const winRate = data.gamesPlayed
                    ? Math.round((data.wins / data.gamesPlayed) * 100)
                    : 0;

                  return (
                    <div
                      key={email}
                      className="grid grid-cols-12 gap-2 p-3 border-b border-slate-700 hover:bg-slate-700/30 transition-colors"
                    >
                      <div className="col-span-4 flex items-center">
                        <span className="text-sm text-white">
                          {data.opponentName || email.split("@")[0]}
                        </span>
                      </div>
                      <div className="col-span-3 flex items-center">
                        <span className="text-sm text-white">
                          {data.wins || 0}W-{data.losses || 0}L-
                          {data.draws || 0}D
                        </span>
                      </div>
                      <div className="col-span-3 flex items-center">
                        <div className="w-full max-w-[100px] bg-slate-700 rounded-full h-1.5 mr-2">
                          <div
                            className={`h-full rounded-full ${
                              winRate >= 60
                                ? "bg-emerald-500"
                                : winRate >= 40
                                ? "bg-amber-500"
                                : "bg-red-500"
                            }`}
                            style={{ width: `${winRate}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-white">{winRate}%</span>
                      </div>
                      <div className="col-span-2 flex items-center">
                        <span className="text-xs text-slate-400">
                          {formatTimeAgo(lastPlayed)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Format time ago function
  const formatTimeAgo = (date) => {
    if (!date) return "N/A";

    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffDay > 30) {
      return date.toLocaleDateString();
    } else if (diffDay > 0) {
      return `${diffDay}d ago`;
    } else if (diffHour > 0) {
      return `${diffHour}h ago`;
    } else if (diffMin > 0) {
      return `${diffMin}m ago`;
    } else {
      return "Just now";
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-xl overflow-hidden border border-slate-700 shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Your Game Stats</h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Tabs navigation */}
          <div className="bg-slate-900 border-b border-slate-700 px-4">
            <div className="flex space-x-1">
              <button
                onClick={() => setActiveTab("overview")}
                className={`py-3 px-4 text-sm font-medium ${
                  activeTab === "overview"
                    ? "text-white border-b-2 border-purple-500"
                    : "text-slate-400 hover:text-white"
                } transition-colors`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`py-3 px-4 text-sm font-medium ${
                  activeTab === "history"
                    ? "text-white border-b-2 border-purple-500"
                    : "text-slate-400 hover:text-white"
                } transition-colors`}
              >
                Game History
              </button>
              <button
                onClick={() => setActiveTab("multiplayer")}
                className={`py-3 px-4 text-sm font-medium ${
                  activeTab === "multiplayer"
                    ? "text-white border-b-2 border-purple-500"
                    : "text-slate-400 hover:text-white"
                } transition-colors`}
              >
                Multiplayer Stats
              </button>
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="h-full flex justify-center items-center">
                <Spinner />
              </div>
            ) : (
              renderTabContent()
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PlayerStatsModal;
